import { type Scale, type ChartOptions } from "chart.js";

import {
  KpiType,
  type ModelMetadataKpiFormula,
} from "@features/mmm/components/explore-budgets/client-setting/types";
import { type User } from "@internal/user";
import { MetricFormatters } from "@north-beam/nb-client-side";

import { chartTooltip } from "./chart-tooltip";
import { OBSERVED_SPEND_ID, REVENUE_ID, ROAS_ID } from "./const";
import { type BarOrLine } from "./mixed-chart";
import { averageAdoptedBudgetMixAnnotation } from "./plugins/average-adopted-mix-spend-annotation";
import { medianWeeklySpendAnnotation } from "./plugins/median-weekly-spend-annotation";
import {
  Y_SCALE_TITLES_PLUGIN_ID,
  type YScaleTitlesPluginPluginOptions,
} from "./plugins/y-scale-titles-plugin";

export const getChartOptions = ({
  user,
  selectedKpi,
  histogramLabels,
  hasDifferentXAxis,
  dateRange,
  annotationArgs: {
    medianSpendIndex,
    medianSpendValue,
    averageAdoptedMixSpendIndex,
    averageAdoptedMixValue,
  },
}: {
  user: User;
  selectedKpi: ModelMetadataKpiFormula;
  histogramLabels: string[];
  hasDifferentXAxis: boolean;
  dateRange: string;
  annotationArgs: {
    medianSpendIndex: number;
    medianSpendValue: string;
    averageAdoptedMixSpendIndex: number | null;
    averageAdoptedMixValue: string | null;
  };
}): ChartOptions<"line"> => {
  const isRevenue = selectedKpi.kpi_type === KpiType.REVENUE;

  return {
    responsive: true,
    maintainAspectRatio: true,
    interaction: {
      mode: hasDifferentXAxis ? ("x" as const) : ("index" as const),
      intersect: false,
      position: "nearest",
    },
    animation: false,
    plugins: {
      legend: { display: false },
      zoom: {
        zoom: {
          wheel: {
            modifierKey: "alt",
            enabled: true,
          },
          drag: {
            modifierKey: "alt",
            enabled: true,
          },
          mode: "x",
        },
      },
      annotation: {
        annotations: {
          ...medianWeeklySpendAnnotation({
            dateRange,
            medianSpendIndex,
            medianSpendValue,
          }),
          ...averageAdoptedBudgetMixAnnotation({
            averageAdoptedMixSpendIndex,
            averageAdoptedMixValue,
          }),
        },
      },
      tooltip: {
        enabled: false,
        external: chartTooltip,
        isRevenue,
        user,
      },
    },
    layout: { padding: { left: 16, right: 20 } },
    [Y_SCALE_TITLES_PLUGIN_ID]: {
      font: {
        family: "Inter",
        size: 12,
        weight: "600",
      },
      titles: {},
    } as YScaleTitlesPluginPluginOptions,
    scales: {
      [REVENUE_ID]: {
        type: "linear" as const,
        display: "auto",
        axis: "y",
        position: "right" as const,
        grace: "50%",
        beginAtZero: true,
        min: 0,
        title: {
          display: true,
          padding: 0,
        },
        grid: {
          drawTicks: false,
          drawBorder: false,
          drawOnChartArea: true,
        },
        ticks: {
          maxTicksLimit: 6,
          padding: 12,
          font: {
            family: "Commons Pro Mono",
            size: 11,
          },
          callback: (val: string | number) =>
            isRevenue
              ? MetricFormatters.currency(
                  Number(val),
                  user.currencies.currentCurrency,
                  {
                    precision: "approximate",
                    totalLength: 0,
                    thousandSeparated: true,
                    average: false,
                  },
                )
              : MetricFormatters.decimal(
                  Number(val),
                  user.currencies.currentCurrency,
                  {
                    precision: "approximate",
                    totalLength: 0,
                    thousandSeparated: true,
                    average: false,
                  },
                ),
        },
      },
      [ROAS_ID]: {
        type: "linear" as const,
        display: "auto",
        axis: "y",
        position: "left" as const,
        grace: "50%",
        beginAtZero: true,
        title: {
          display: true,
          padding: 8,
        },
        grid: {
          drawTicks: false,
          drawBorder: false,
          drawOnChartArea: false,
        },
        ticks: {
          maxTicksLimit: 6,
          padding: 12,
          font: {
            family: "Commons Pro Mono",
            size: 11,
          },
          callback: (val: string | number) =>
            isRevenue
              ? MetricFormatters.multiplier(
                  Number(val),
                  user.currencies.currentCurrency,
                  {
                    precision: "approximate",
                    totalLength: 0,
                    thousandSeparated: true,
                    average: false,
                  },
                )
              : MetricFormatters.currency(
                  Number(val),
                  user.currencies.currentCurrency,
                  {
                    precision: "approximate",
                    totalLength: 0,
                    thousandSeparated: true,
                    average: false,
                  },
                ),
        },
      },
      [OBSERVED_SPEND_ID]: {
        type: "linear" as const,
        axis: "y",
        position: "right" as const,
        grace: "20%",
        suggestedMax: 3,
        display: "auto",
        beginAtZero: true,
        alignToPixels: true,
        title: {
          display: false,
          padding: 0,
        },
        grid: {
          display: false,
          drawTicks: false,
          drawBorder: false,
          drawOnChartArea: false,
        },
        ticks: {
          maxTicksLimit: 6,
          font: {
            family: "Commons Pro Mono",
            size: 11,
          },
        },
      },
      x: {
        axis: "x",
        grid: {
          drawOnChartArea: false,
          drawBorder: false,
          drawTicks: false,
        },
        ticks: {
          padding: 20,
          maxTicksLimit: 8,
          maxRotation: 0,
          autoSkip: true,
          font: {
            family: "Commons Pro",
            size: 13,
          },
          callback: function (idx: number) {
            return MetricFormatters.currency(
              // Fixes typing issue on previously-existing code
              // TS doesn't know what "this" is
              Number((this as unknown as Scale).getLabelForValue(idx)),
              user.currencies.currentCurrency,
              {
                precision: "approximate",
                totalLength: 0,
                thousandSeparated: true,
                average: false,
              },
            );
          },
        },
      },
      histogramX: {
        labels: histogramLabels,
        type: "category",
        axis: "x",
        display: false,
        grid: {
          drawOnChartArea: false,
          drawBorder: false,
          drawTicks: false,
          display: false,
        },
        ticks: {
          maxRotation: 0,
          autoSkip: false,
          display: false,
        },
      },
    },
  } as unknown as ChartOptions<BarOrLine>;
  // NOTE: have to go to unknown to allow "position" to be added to "interaction"
  // options. This was there before this PR but for some reason TS wasn't complainig
  // about it. Verifvied both (1) "position" does not exist in typing provided from package
  // (2) "position" property DOES affect the interaction of the chart (and does what we want it to do)
  // TODO: update typing so cast to unknown first is not necessary
};
